import PageContainer from 'components/PageContainer';
import Table, { TableActions } from 'components/Table';
import useTaxYears from 'hooks/data/taxReport/useTaxYears';
import { dateToLabel, formatTaxYear } from 'further-ui/utils';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { TaxYearStatus } from 'further-types/annual-tax-report';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { FC } from 'react';
import { useGetRole } from 'hooks/ui/useGetRole';
import Switch from '@mui/material/Switch';
import { makeStyles } from 'tss-react/mui';
import { CloudDownload } from '@mui/icons-material';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import AlertDialog from 'components/AlertDialog';
import PageContent from 'components/PageContent';
import { styled } from '@mui/material';
import PageFilters, { Filter } from 'components/PageFilters';
import { FirmSelector } from 'components/FirmSelector';
import RecordView from 'components/RecordView';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type TaxYear = ReturnType<
  typeof useTaxYears
  //@ts-expect-error
>['taxYears']['data']['results'][number];

const useStyles = makeStyles()(() => ({
  switch: {
    display: 'flex',
    gridGap: '0.5rem',
  },
  date: {
    minWidth: '140px',
  },
}));

const ActionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'start',
});

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Investor reporting' },
  {
    label: 'Tax report audit',
    isActive: true,
  },
];

const TaxReportAudit: FC = () => {
  const { classes } = useStyles();
  const { isSuperAdmin } = useGetRole();
  const approvalDisclosure = useDisclosure<{ taxYear: TaxYear }>();
  const [canEditTaxReport] = useHasPermissions([RolePermission.EditTaxReport]);

  const pagination = usePagination({
    orderBy: 'taxYearStart',
    order: 'desc',
    id: 'tax-report-audit',
  });

  const { filters, handleChangeFilters } = useFilters<{
    firmId?: string;
  }>('tax-report-audit-table', {
    defaultFilters: {
      firmId: undefined,
    },
    onFiltersChange: pagination.toFirstPage,
  });

  const { page, rowsPerPage } = pagination;
  const { taxYears, updateTaxYear } = useTaxYears({
    page,
    rowsPerPage,
    ...(filters.firmId && { firmId: filters.firmId }),
  });
  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleDownload = (taxYearStart: number, firmId: string) => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.TaxReportAuditExport,
      firmId,
      params: {
        taxYearStart,
      },
    });
  };

  const handleUpdate = (taxYear: TaxYear, status: TaxYearStatus) =>
    updateTaxYear.mutate({
      taxYearStart: taxYear.taxYearStart,
      firmId: taxYear.firmId,
      status,
    });

  const columns = [
    {
      label: 'Tax year',
      sort: false,
      key: 'taxYear',
      render: (elm: TaxYear) =>
        `${isSuperAdmin ? `${elm.firmName} - ` : ''}${formatTaxYear(
          elm.taxYearStart,
          elm.taxYearStart + 1,
        )}`,
    },
    {
      label: 'Approved on',
      sort: false,
      key: 'approvedAt',
      render: (elm: TaxYear) => (
        <div className={isSuperAdmin ? classes.date : undefined}>
          {dateToLabel(elm.approvedAt)}
        </div>
      ),
    },
    {
      label: 'Status',
      sort: false,
      key: 'status',
      render: (elm: TaxYear) => (
        <div className={classes.switch}>
          <span>Unapproved</span>
          <Switch
            disabled={
              elm.status === TaxYearStatus.Approved ||
              updateTaxYear.isPending ||
              !canEditTaxReport
            }
            checked={elm.status === TaxYearStatus.Approved}
            onChange={(e) => {
              if (e.target.checked) {
                approvalDisclosure.onOpen();
                approvalDisclosure.stageAction({
                  taxYear: elm,
                });
              }
            }}
          />
          <span>Approved</span>
        </div>
      ),
    },
    {
      label: 'Action',
      key: 'actions',
      sort: false,
      render: (elm: TaxYear) => (
        <ActionContainer>
          <TableActions
            showAsDropdown
            actions={[
              {
                label: 'Download report',
                color: 'primary',
                icon: CloudDownload,
                onClick: () => handleDownload(elm.taxYearStart, elm.firmId),
                disabled: isExporting,
              },
            ]}
          />
        </ActionContainer>
      ),
    },
  ];

  return (
    <PageContainer heading={'Tax report audit'} breadcrumbs={breadcrumbs}>
      <PageContent>
        {isSuperAdmin && (
          <RecordView size="small">
            <PageFilters pageId="tax-report-audit-table">
              <Filter>
                <FirmSelector
                  firmId={filters.firmId}
                  placeholder="Select firm"
                  onChange={(firmId) => {
                    handleChangeFilters({ firmId });
                  }}
                />
              </Filter>
            </PageFilters>
          </RecordView>
        )}

        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={taxYears.data?.results ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          pagination={true}
          count={taxYears.data?.total ?? 0}
          loading={taxYears.isFetching}
          variant="nohover"
        />
        <AlertDialog
          open={approvalDisclosure.isOpen}
          onClose={approvalDisclosure.onClose}
          onConfirm={() => {
            handleUpdate(
              approvalDisclosure.actionProps.taxYear,
              TaxYearStatus.Approved,
            );
            approvalDisclosure.onClose();
          }}
          title="Approve report data"
          content={
            <p>
              Do you wish to approve your tax report data? You cannot undo this
              action once it is confirmed.
            </p>
          }
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Approve',
          }}
        />
      </PageContent>
    </PageContainer>
  );
};
export default TaxReportAudit;
